import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { getApiBaseUrl, getAuthToken } from "@/lib/api";
import {
  Archive,
  ArchiveRestore,
  Download,
  FileText,
  Loader2,
  Paperclip,
  Save,
  Trash2,
} from "lucide-react";
import {
  CONTRACT_ALERT_LABELS,
  CONTRACT_ALERT_TONES,
  deleteSupplierContract,
  describeExpiry,
  listSupplierContracts,
  setSupplierContractArchived,
  updateSupplierContract,
  uploadSupplierContract,
  type SupplierContract,
} from "@/lib/suppliersApi";

type SupplierContractsPanelProps = {
  supplierId: number;
  /** CEO / General Manager. Everyone else can attach and edit, but not delete. */
  canDelete: boolean;
  onContractsChanged?: () => void;
};

const formatFileSize = (bytes?: number | null) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const SupplierContractsPanel = ({
  supplierId,
  canDelete,
  onContractsChanged,
}: SupplierContractsPanelProps) => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [contracts, setContracts] = useState<SupplierContract[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [notes, setNotes] = useState("");

  /** Local edits to each contract's dates, keyed by contract id. */
  const [dateDrafts, setDateDrafts] = useState<Record<string, { start: string; expiry: string }>>({});

  const load = async () => {
    try {
      setLoading(true);
      const items = await listSupplierContracts(supplierId);
      setContracts(items);
      setDateDrafts(Object.fromEntries(items.map((contract) => [
        contract.id,
        { start: contract.start_date ?? "", expiry: contract.expiry_date ?? "" },
      ])));
    } catch (error: any) {
      toast({
        title: "Unable to load contracts",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierId]);

  const resetUploadForm = () => {
    setFile(null);
    setTitle("");
    setStartDate("");
    setExpiryDate("");
    setNotes("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!file || uploading) return;
    try {
      setUploading(true);
      await uploadSupplierContract(supplierId, file, {
        title: title.trim() || undefined,
        startDate: startDate || null,
        expiryDate: expiryDate || null,
        notes: notes.trim() || null,
      });
      toast({
        title: "Contract attached",
        description: expiryDate
          ? "Expiry alerts will start one month before the date."
          : "No expiry date set, so this contract will not raise alerts.",
      });
      resetUploadForm();
      await load();
      onContractsChanged?.();
    } catch (error: any) {
      toast({
        title: "Unable to attach contract",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSaveDates = async (contract: SupplierContract) => {
    const draft = dateDrafts[contract.id];
    if (!draft || savingId) return;
    try {
      setSavingId(contract.id);
      await updateSupplierContract(contract.id, {
        title: contract.title,
        start_date: draft.start || null,
        expiry_date: draft.expiry || null,
        notes: contract.notes,
      });
      toast({ title: "Contract dates updated" });
      await load();
      onContractsChanged?.();
    } catch (error: any) {
      toast({
        title: "Unable to update contract",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleToggleArchive = async (contract: SupplierContract) => {
    try {
      setSavingId(contract.id);
      await setSupplierContractArchived(contract.id, !contract.archived);
      toast({
        title: contract.archived ? "Contract reactivated" : "Contract archived",
        description: contract.archived
          ? "It will raise expiry alerts again."
          : "Archived contracts are kept on file but stop raising alerts.",
      });
      await load();
      onContractsChanged?.();
    } catch (error: any) {
      toast({
        title: "Unable to update contract",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (contract: SupplierContract) => {
    if (!window.confirm(`Delete "${contract.title || contract.file_name}"? The file is removed permanently.`)) {
      return;
    }
    try {
      await deleteSupplierContract(contract.id);
      toast({ title: "Contract deleted" });
      await load();
      onContractsChanged?.();
    } catch (error: any) {
      toast({
        title: "Unable to delete contract",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    }
  };

  // The file endpoints are token-authenticated, so a plain <a href> would 401. Fetch with the
  // header and hand the browser a blob URL instead.
  const openFile = async (contract: SupplierContract, asDownload: boolean) => {
    try {
      const path = asDownload ? contract.download_url : contract.view_url;
      const response = await fetch(`${getApiBaseUrl()}${path.replace(/^\/api/, "")}`, {
        headers: { Authorization: `Bearer ${getAuthToken() ?? ""}` },
      });
      if (!response.ok) throw new Error("Failed to fetch the file");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      if (asDownload) {
        const link = document.createElement("a");
        link.href = url;
        link.download = contract.file_name || "contract.pdf";
        link.click();
      } else {
        window.open(url, "_blank", "noopener");
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error: any) {
      toast({
        title: "Unable to open the contract",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 rounded-lg border bg-muted/30 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Paperclip className="h-4 w-4" />
          Attach a contract
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="contract-file">File (PDF or Word, up to 10 MB)</Label>
            <Input
              id="contract-file"
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,application/pdf"
              onChange={(event) => setFile(event.target.files?.[0] ?? null)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contract-title">Title</Label>
            <Input
              id="contract-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Defaults to the file name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contract-start">Start date</Label>
            <Input
              id="contract-start"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contract-expiry">Expiry date</Label>
            <Input
              id="contract-expiry"
              type="date"
              value={expiryDate}
              onChange={(event) => setExpiryDate(event.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">
              Alerts fire at 1 month, 2 weeks, 1 week and 3 days before this date.
            </p>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="contract-notes">Notes</Label>
            <Textarea
              id="contract-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={2}
              placeholder="Commercial terms, renewal conditions..."
            />
          </div>
        </div>
        <div>
          <Button type="button" onClick={() => { void handleUpload(); }} disabled={!file || uploading}>
            {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Paperclip className="mr-2 h-4 w-4" />}
            {uploading ? "Attaching..." : "Attach contract"}
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading contracts...
        </div>
      )}

      {!loading && contracts.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          No contracts on file for this supplier yet.
        </p>
      )}

      <div className="space-y-3">
        {contracts.map((contract) => {
          const draft = dateDrafts[contract.id] ?? { start: "", expiry: "" };
          const dirty =
            draft.start !== (contract.start_date ?? "") || draft.expiry !== (contract.expiry_date ?? "");
          return (
            <div
              key={contract.id}
              className={`rounded-lg border p-4 ${contract.archived ? "bg-muted/40 opacity-75" : ""}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="font-medium">{contract.title || contract.file_name}</span>
                    {contract.archived && <Badge variant="secondary">Archived</Badge>}
                    {!contract.archived && contract.alert_threshold && (
                      <Badge
                        variant="outline"
                        className={CONTRACT_ALERT_TONES[contract.alert_threshold]}
                      >
                        {CONTRACT_ALERT_LABELS[contract.alert_threshold]}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {describeExpiry(contract)}
                    {contract.file_size ? ` · ${formatFileSize(contract.file_size)}` : ""}
                    {contract.uploaded_by_name ? ` · added by ${contract.uploaded_by_name}` : ""}
                  </p>
                  {contract.notes && <p className="text-sm">{contract.notes}</p>}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button type="button" variant="ghost" size="icon" title="Open" onClick={() => { void openFile(contract, false); }}>
                    <FileText className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="ghost" size="icon" title="Download" onClick={() => { void openFile(contract, true); }}>
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    title={contract.archived ? "Reactivate" : "Archive"}
                    onClick={() => { void handleToggleArchive(contract); }}
                    disabled={savingId === contract.id}
                  >
                    {contract.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                  </Button>
                  {canDelete && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      title="Delete"
                      className="text-destructive hover:text-destructive"
                      onClick={() => { void handleDelete(contract); }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Start date</Label>
                  <Input
                    type="date"
                    value={draft.start}
                    onChange={(event) =>
                      setDateDrafts((prev) => ({ ...prev, [contract.id]: { ...draft, start: event.target.value } }))
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] text-muted-foreground">Expiry date</Label>
                  <Input
                    type="date"
                    value={draft.expiry}
                    onChange={(event) =>
                      setDateDrafts((prev) => ({ ...prev, [contract.id]: { ...draft, expiry: event.target.value } }))
                    }
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { void handleSaveDates(contract); }}
                    disabled={!dirty || savingId === contract.id}
                  >
                    {savingId === contract.id ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}
                    Save dates
                  </Button>
                </div>
              </div>
              {dirty && (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Changing the expiry date resets the alerts, so this contract will warn again from 1 month out.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SupplierContractsPanel;
